import { supabase } from "@/integrations/supabase/client";
import { DogSchool } from "@/data/dogSchools";
import { dogSchools } from "@/data/dogSchools";
import { mapSupabaseRowToDogSchool } from "@/utils/supabaseMappers";
import { determineStateFromPostalCode } from "@/utils/locationUtils";
import { sortSchoolsByReviewCount } from "../utils/sortUtils";
import { fetchDogSchoolsByCountry } from "../country/countryService";
import { getCountryName } from "../utils/countryUtils";

/**
 * Fetch dog schools by state from Supabase
 */
export const fetchDogSchoolsByState = async (country: string, state: string): Promise<DogSchool[]> => {
  try {
    console.log(`Fetching dog schools for country: ${country}, state: ${state}`);
    
    // Convert country code to full name if needed
    const normalizedCountry = country === 'de' ? 'Germany' : 
                            country === 'at' ? 'Austria' : 
                            country === 'ch' ? 'Switzerland' : country;
    
    console.log(`Normalized country: ${normalizedCountry}`);
    
    // Exakte Bundesland-Zuordnung mit verschiedenen Schreibweisen
    const stateMapping: Record<string, string> = {
      // Deutschland
      'baden-wurttemberg': 'Baden-Württemberg',
      'baden-wuerttemberg': 'Baden-Württemberg',
      'baden-württemberg': 'Baden-Württemberg',
      'bayern': 'Bayern',
      'berlin': 'Berlin',
      'brandenburg': 'Brandenburg',
      'bremen': 'Bremen',
      'hamburg': 'Hamburg',
      'hessen': 'Hessen',
      'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
      'niedersachsen': 'Niedersachsen',
      'nordrhein-westfalen': 'Nordrhein-Westfalen',
      'rheinland-pfalz': 'Rheinland-Pfalz',
      'saarland': 'Saarland',
      'sachsen': 'Sachsen',
      'sachsen-anhalt': 'Sachsen-Anhalt',
      'schleswig-holstein': 'Schleswig-Holstein',
      'thuringen': 'Thüringen',
      'thueringen': 'Thüringen',
      'thüringen': 'Thüringen',
      
      // Österreich
      'burgenland': 'Burgenland',
      'kaernten': 'Kärnten',
      'kärnten': 'Kärnten',
      'niederoesterreich': 'Niederösterreich',
      'niederösterreich': 'Niederösterreich',
      'oberoesterreich': 'Oberösterreich',
      'oberösterreich': 'Oberösterreich',
      'salzburg': 'Salzburg',
      'steiermark': 'Steiermark',
      'tirol': 'Tirol',
      'vorarlberg': 'Vorarlberg',
      'wien': 'Wien'
    };

    // Normalisiere den State-Namen zu der exakten Schreibweise
    const exactStateName = stateMapping[state.toLowerCase().replace(/-/g, '-')] || state;
    console.log(`Exact state name: ${exactStateName}`);
    
    // Exakte Suche nach Bundesland mit OR-Bedingung für verschiedene Schreibweisen
    const { data, error } = await supabase
      .from('hundeschuleList')
      .select('*')
      .eq('country', normalizedCountry)
      .or(`state.eq.${exactStateName},state.eq.${exactStateName.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue')},state.eq.${exactStateName.replace(/ae/g, 'ä').replace(/oe/g, 'ö').replace(/ue/g, 'ü')}`)
      .order('reviewscount', { ascending: false });
    
    if (error) {
      console.error("Error fetching dog schools for state:", error);
      return [];
    }
    
    if (!data || data.length === 0) {
      console.log(`No schools found for state: ${exactStateName}`);
      return [];
    }
    
    console.log(`Found ${data.length} schools for state: ${exactStateName}`);
    
    // Map to DogSchool objects and ensure review data is properly set
    const mappedSchools = data.map(row => {
      const school = mapSupabaseRowToDogSchool(row);
      
      // Ensure review count is properly set
      if (row.reviewscount) {
        try {
          const parsedCount = parseInt(String(row.reviewscount).replace(/[^\d]/g, ''), 10);
          if (!isNaN(parsedCount)) {
            school.reviewCount = parsedCount;
          }
        } catch (e) {
          console.log(`Failed to parse review count: ${row.reviewscount}`, e);
        }
      }
      
      // Ensure rating is properly set
      if (row.reviewsrating) {
        try {
          const parsedRating = parseFloat(String(row.reviewsrating).replace(',', '.'));
          if (!isNaN(parsedRating)) {
            school.googleReview = parsedRating;
          }
        } catch (e) {
          console.log(`Failed to parse rating: ${row.reviewsrating}`, e);
        }
      }
      
      return school;
    });
    
    return sortSchoolsByReviewCount(mappedSchools);
    
  } catch (error) {
    console.error("Error in fetchDogSchoolsByState:", error);
    return [];
  }
};
